import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { ContactApiService } from '@services/contacts/contact/contact-api.service';
import { IContact } from '@models/contacts/hrm/contacts';

@Component({
  selector: 'lib-contact-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './contact-selector.component.html',
  styleUrls: ['./contact-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactSelectorComponent),
      multi: true
    }
  ]
})
export class ContactSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private contactService = inject(ContactApiService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router, { optional: true });

  // Include / Exclude Contact Types (Reactive setters)
  private _contactTypes: string[] = [];
  @Input() set contactTypes(val: string[]) {
    this._contactTypes = val || [];
    this.filterContacts();
  }
  get contactTypes(): string[] {
    return this._contactTypes;
  }

  private _excludeContactTypes: string[] = [];
  @Input() set excludeContactTypes(val: string[]) {
    this._excludeContactTypes = val || [];
    this.filterContacts();
  }
  get excludeContactTypes(): string[] {
    return this._excludeContactTypes;
  }

  // Account Status Filter: 'open' | 'closed' | 'all'
  private _accountStatus: 'open' | 'closed' | 'all' = 'open';
  @Input() set accountStatus(val: 'open' | 'closed' | 'all') {
    this._accountStatus = val || 'open';
    this.filterContacts();
  }
  get accountStatus(): 'open' | 'closed' | 'all' {
    return this._accountStatus;
  }

  // Prioritize recently used contacts at the top
  @Input() prioritizeRecent = true;

  // Show financial due balance in dropdown options
  @Input() showDueBalance = true;

  // Toggle to bind the full IContact object or ID (default is ID for form controls)
  @Input() bindObject = false;

  // Show Quick "+ New Contact" action in footer
  @Input() showQuickAdd = true;

  // Show header bar with quick filters inside dropdown
  @Input() showHeaderFilter = true;

  // Standard ng-select inputs
  @Input() placeholder = 'Select Contact';
  @Input() multiple = false;
  @Input() clearable = true;
  @Input() appendToBody = false;

  @Output() contactSelected = new EventEmitter<IContact | null>();
  @Output() newContactClicked = new EventEmitter<void>();

  contacts: IContact[] = [];
  filteredContacts: IContact[] = [];
  recentContactIds: number[] = [];
  value: any = null;
  isDisabled = false;
  loading = false;

  baseOpenCount = 0;
  baseClosedCount = 0;
  baseTotalCount = 0;

  private sub = new Subscription();

  onChange: any = () => {};
  onTouched: any = () => {};

  customSearchFn = (term: string, item: IContact) => {
    term = (term || '').toLowerCase().trim();
    if (!term) return true;

    const cleanIdTerm = term.startsWith('#') ? term.substring(1) : term;
    const rawTerm = term.replace(/[\s\-\(\)\+]/g, '');
    const phoneStr = item.phone ? item.phone.toString() : '';

    return (
      (item.name?.toLowerCase().includes(term) ?? false) ||
      (item.contact_id?.toString().includes(cleanIdTerm) ?? false) ||
      (phoneStr.includes(rawTerm) || phoneStr.includes(term)) ||
      (item.gst?.toLowerCase().includes(term) ?? false) ||
      (item.email?.toLowerCase().includes(term) ?? false) ||
      (item.billing_address?.address_city?.toLowerCase().includes(term) ?? false) ||
      (item.contact_type?.contact_type_name?.toLowerCase().includes(term) ?? false) ||
      (item.contact_type?.contact_type_code?.toLowerCase().includes(term) ?? false)
    );
  };

  ngOnInit(): void {
    this.loadRecentContactIds();
    this.loading = true;
    this.sub.add(
      this.contactService.contacts$.subscribe(contacts => {
        this.contacts = contacts || [];
        this.loading = false;
        this.filterContacts();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  filterContacts(): void {
    let baseList = [...this.contacts];

    // Filter by contactTypes (include)
    if (this._contactTypes && this._contactTypes.length > 0) {
      baseList = baseList.filter(c =>
        this._contactTypes.includes(c.contact_type?.contact_type_code || '')
      );
    }

    // Filter by excludeContactTypes (exclude)
    if (this._excludeContactTypes && this._excludeContactTypes.length > 0) {
      baseList = baseList.filter(c =>
        !this._excludeContactTypes.includes(c.contact_type?.contact_type_code || '')
      );
    }

    this.baseTotalCount = baseList.length;
    this.baseOpenCount = baseList.filter(c => c.account_status !== 1).length;
    this.baseClosedCount = baseList.filter(c => c.account_status === 1).length;

    let result = baseList;

    // Filter by accountStatus ('open' | 'closed' | 'all')
    if (this._accountStatus === 'open') {
      result = result.filter(c => {
        if (this.isSelected(c.contact_id)) return true; // Keep selected contact visible
        return c.account_status !== 1;
      });
    } else if (this._accountStatus === 'closed') {
      result = result.filter(c => {
        if (this.isSelected(c.contact_id)) return true;
        return c.account_status === 1;
      });
    }

    // Sort: Prioritize recent contacts on top if enabled
    if (this.prioritizeRecent && this.recentContactIds.length > 0) {
      const recentIndexMap = new Map<number, number>();
      this.recentContactIds.forEach((id, index) => recentIndexMap.set(id, index));

      result.sort((a, b) => {
        const aRecent = recentIndexMap.has(a.contact_id);
        const bRecent = recentIndexMap.has(b.contact_id);

        if (aRecent && bRecent) {
          return recentIndexMap.get(a.contact_id)! - recentIndexMap.get(b.contact_id)!;
        }
        if (aRecent) return -1;
        if (bRecent) return 1;

        return (a.name || '').localeCompare(b.name || '');
      });
    }

    this.filteredContacts = result;
    this.cdr.markForCheck();
  }

  setAccountStatusFilter(status: 'open' | 'closed' | 'all', event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this._accountStatus = status;
    this.filterContacts();
    this.cdr.markForCheck();
  }

  isSelected(contactId: number): boolean {
    if (this.value === null || this.value === undefined) return false;
    if (this.multiple && Array.isArray(this.value)) {
      return this.value.some((v: any) =>
        typeof v === 'object' && v !== null ? v.contact_id === contactId : v === contactId
      );
    }
    return typeof this.value === 'object' && this.value !== null
      ? this.value.contact_id === contactId
      : this.value === contactId;
  }

  writeValue(value: any): void {
    this.value = value;
    this.filterContacts();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onSelectChange(event: any): void {
    if (this.multiple) {
      const items = event || [];
      const emittedValue = this.bindObject ? items : items.map((item: any) => item.contact_id);
      this.value = emittedValue;
      this.onChange(emittedValue);
      this.contactSelected.emit(event || []);
    } else {
      const emittedValue = event ? (this.bindObject ? event : event.contact_id) : null;
      this.value = emittedValue;
      this.onChange(emittedValue);
      if (event?.contact_id) {
        this.saveRecentContactId(event.contact_id);
      }
      this.contactSelected.emit(event || null);
    }
    this.onTouched();
    this.cdr.markForCheck();
  }

  onClear(): void {
    this.value = null;
    this.onChange(null);
    this.onTouched();
    this.contactSelected.emit(null);
    this.cdr.markForCheck();
  }

  quickAddContact(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.newContactClicked.observed) {
      this.newContactClicked.emit();
    } else if (this.router) {
      this.router.navigate(['/hrm/contacts/create']);
    }
  }

  onDropdownOpen(): void {
    this.loadRecentContactIds();
    this.filterContacts();
  }

  isRecent(contactId: any): boolean {
    if (!contactId || !this.prioritizeRecent) return false;
    return this.recentContactIds.includes(Number(contactId));
  }

  getInitials(name?: string): string {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  getAvatarColor(name?: string): string {
    if (!name) return '#64748b';
    const colors = [
      '#2563eb', // blue
      '#7c3aed', // purple
      '#db2777', // pink
      '#ea580c', // orange
      '#059669', // emerald
      '#0891b2', // cyan
      '#4f46e5', // indigo
      '#d9214e'  // mavles red
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  private loadRecentContactIds(): void {
    this.recentContactIds = this.contactService.getRecentContactIds();
  }

  private saveRecentContactId(id: number): void {
    if (!id) return;
    this.contactService.recordRecentContact(id);
    this.loadRecentContactIds();
  }
}
